/* The Plan catalogue and what an Organisation is using — STORY-031.
 *
 * `pricing.md` owns the numbers; nothing here restates them. What this file
 * fixes is the SHAPE, and one decision in it matters: a limit is `number | null`
 * and null means unlimited. A sentinel would be a limit somebody eventually
 * hits, and every comparison against it would read as though it were real.
 */
import { z } from 'zod';
export const planSchema = z.object({
    code: z.string(),
    /* Integer minor units plus a currency, never a decimal — ADR-0005. */
    monthlyPriceMinor: z.number().int(),
    annualPriceMinor: z.number().int(),
    currency: z.string().length(3),
    /** Null is unlimited. */
    activeEventLimit: z.number().int().nullable(),
    seatLimit: z.number().int().nullable(),
    contactLimit: z.number().int().nullable(),
});
export const plansResponseSchema = z.object({ plans: z.array(planSchema) });
/**
 * What one Organisation is currently using, against the limits of its plan.
 *
 * STORY-031: a Member who does not know they are one Event from a wall will
 * experience the limit as a bug. So usage is readable BEFORE anything refuses.
 */
/** One number per meter. Null limits mean the plan sets no ceiling. */
const perMeter = (value) => z.object({ activeEvents: value, seats: value, contacts: value });
export const usageResponseSchema = z.object({
    plan: z.string(),
    activeEvents: z.number().int(),
    seats: z.number().int(),
    contacts: z.number().int(),
    /**
     * The current plan's ceilings, and how far past them the workspace is —
     * STORY-045.
     *
     * **Added rather than folded into the three counts above**, which stay plain
     * numbers: changing their shape would break every client already reading
     * them, and `git.md` makes API changes additive by default.
     *
     * The ceilings travel because a client cannot know them — they come from the
     * catalogue, and a client with its own copy disagrees the moment a plan
     * changes. `overBy` travels for the same reason the refusal carries it: it
     * is zero when a meter is merely full and positive only when the workspace is
     * genuinely over, and those are two different sentences.
     */
    limits: perMeter(z.number().int().nullable()),
    overBy: perMeter(z.number().int().nonnegative()),
});
//# sourceMappingURL=plans.js.map